import React, { useEffect, useState } from 'react';
import { CheckCircle } from '@phosphor-icons/react';
import SearchableSelect from './SearchableSelect';
import Toast from './Toast';
import SectionLock from './SectionLock';
import {
  fetchBranches, fetchGeneralReceipts, fetchGeneralReceiptDetails, fetchGeneralReceiptAmount,
  submitReverseGeneralReceipt, saveData,
} from '../api/connectionRegistryApi';
import { useLoginName } from '../utils/auditUser';
import { NO_DATA_MESSAGE, isEmptyResult, inlineNoticeColor } from '../utils/noData';

function GeneralReceiptCancelForm({ connectionId }) {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);
  const [branches, setBranches] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [branchCode, setBranchCode] = useState('');
  const [receiptNo, setReceiptNo] = useState('');
  const [header, setHeader] = useState(null);
  const [rows, setRows] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [auditFailed, setAuditFailed] = useState(false);
  // A failed cancel blocks the screen until acknowledged, matching every other
  // Supporting Tools screen. Success is handled by the panel below instead, which
  // already replaces the form and needs an explicit "Start another request".
  const [toast, setToast] = useState(null);

  const reasonInvalid = touched && (reason.trim().length < 10 || reason.length > 200);
  const isValid = selectedIdx >= 0 && reason.trim().length >= 10 && reason.length <= 200;

  useEffect(() => {
    if (!connectionId) return;
    (async () => {
      try {
        setBranches(await fetchBranches(connectionId));
      } catch (e) {
        setLoadError('Could not load branches from the client API');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionId]);

  const handleBranch = async (value) => {
    setBranchCode(value);
    setReceiptNo('');
    setReceipts([]);
    setHeader(null);
    setRows([]);
    setSelectedIdx(-1);
    if (!value) return;
    try {
      const list = await fetchGeneralReceipts(connectionId, value);
      setReceipts(list);
      // This screen reports through the inline banner rather than a toast.
      if (isEmptyResult(list)) setLoadError(NO_DATA_MESSAGE);
    } catch (e) {
      setLoadError('Could not load general receipts for that branch');
    }
  };

  const handleReceipt = (value) => {
    setReceiptNo(value);
    setHeader(null);
    setRows([]);
    setSelectedIdx(-1);
  };

  const handleGet = async () => {
    const receiptEntry = receipts.find((r) => r.general_receipt_number === receiptNo);
    if (!receiptEntry) return;
    setLoading(true);
    setLoadError('');
    try {
      const detailsRows = await fetchGeneralReceiptDetails(connectionId, branchCode, receiptNo, receiptEntry.interbranch_id);
      if (!detailsRows.length) {
        setLoadError('No details found for this receipt.');
        return;
      }
      const h = detailsRows[0];
      setHeader(h);
      const amountRows = await fetchGeneralReceiptAmount(connectionId, branchCode, h.deposited_reference_no);
      setRows(amountRows || []);
      setSelectedIdx(-1);
      if (isEmptyResult(amountRows)) setLoadError(NO_DATA_MESSAGE);
    } catch (e) {
      setLoadError('Could not load receipt amount details.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setTouched(true);
    if (!isValid || saving) return;
    const row = rows[selectedIdx];
    setSaving(true);
    setSubmitError('');
    try {
      await submitReverseGeneralReceipt(connectionId, {
        branchCode,
        caoSchema: header.caoname,
        groupCode: header.groupcode,
        ticketNo: header.ticketno,
        contactId: row.contact_id,
        generalReceiptNumber: receiptNo,
        commonReceiptNumber: header.comman_receipt_number,
        paymentNumber: row.payment_number,
        paymentDate: row.payment_date,
        paymentVoucherId: row.tbl_trans_payment_voucher_id,
        paymentVoucherDetailsId: row.tbl_trans_payment_voucher_details_id,
        reason,
      });
      // The reversal already went through, so an audit failure must not read as a
      // failed cancel — it is reported inside the success panel instead.
      try {
        await saveData([{
          schemaName: branchCode || null,
          loginName,
          loginTime: new Date().toISOString(),
          changeType: 'GENERAL_RECEIPT_CANCEL',
          oldData: {
            branchCode,
            caoSchema: header.caoname,
            groupCode: header.groupcode,
            ticketNo: header.ticketno,
            contactId: row.contact_id,
            generalReceiptNumber: receiptNo,
            commonReceiptNumber: header.comman_receipt_number,
            paymentNumber: row.payment_number,
            paymentDate: row.payment_date,
            paymentVoucherId: row.tbl_trans_payment_voucher_id,
            paymentVoucherDetailsId: row.tbl_trans_payment_voucher_details_id,
            status: 'ACTIVE',
          },
          newData: { generalReceiptNumber: receiptNo, status: 'CANCELLED' },
          reason: reason.trim(),
        }]);
        setAuditFailed(false);
      } catch (auditErr) {
        console.error('Audit save failed:', auditErr);
        setAuditFailed(true);
      }
      setSaved(true);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Could not cancel this receipt. Please try again.';
      setSubmitError(detail);
      setToast({ severity: 'error', summary: 'Cancel Failed', detail, sticky: true });
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setBranchCode('');
    setReceiptNo('');
    setReceipts([]);
    setHeader(null);
    setRows([]);
    setSelectedIdx(-1);
    setLoadError('');
    setReason('');
    setTouched(false);
    setSaved(false);
    setSubmitError('');
    setAuditFailed(false);
  };

  if (saved) {
    return (
      <div
        style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '16px', border: '1px solid #A7F3D0', background: '#ECFDF5', borderRadius: '8px' }}
        data-testid="general-receipt-cancel-success"
      >
        <CheckCircle size={22} color="#059669" weight="fill" />
        <div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#065F46' }}>Receipt cancellation captured</div>
          <div style={{ fontSize: '12px', color: '#065F46', marginTop: '2px' }}>Receipt {receiptNo} reversed.</div>
          {auditFailed && (
            <div style={{ fontSize: '12px', color: '#92400E', marginTop: '6px' }} data-testid="general-receipt-cancel-audit-warning">
              The audit log entry failed to save — this change will not appear in Reports of Updates. Please check logs.
            </div>
          )}
          <button type="button" onClick={handleClear} className="btn-secondary" style={{ marginTop: '12px', padding: '6px 12px', fontSize: '12px' }} data-testid="general-receipt-cancel-new-request">
            Start Another Request
          </button>
        </div>
      </div>
    );
  }

  return (
    <div key={formKey} style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px' }} data-testid="general-receipt-cancel-form">
      <Toast toast={toast} onClose={() => setToast(null)} testId="general-receipt-cancel-toast" />

      <div style={{ marginBottom: '4px', fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Change Request</div>
      <div style={{ marginBottom: '20px', fontSize: '16px', fontWeight: '700', color: '#111827' }}>General Receipt Cancel</div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
        <div>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Receipt</div>
          <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose branch and receipt number, then click Get to fetch details</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '12px', alignItems: 'end' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Branch Name *</label>
          <SearchableSelect
            options={branches}
            value={branchCode}
            onChange={handleBranch}
            getLabel={(b) => b.branch_name}
            getValue={(b) => b.branch_code}
            placeholder="Search branch..."
            testId="general-receipt-cancel-branch"
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Receipt No. *</label>
          <SearchableSelect
            options={receipts}
            value={receiptNo}
            onChange={handleReceipt}
            getLabel={(r) => r.general_receipt_number}
            getValue={(r) => String(r.general_receipt_number)}
            placeholder={'Search receipt...'}
            disabled={!branchCode}
            testId="general-receipt-cancel-receipt"
          />
        </div>
        <div>
          <button type="button" className="btn-primary" disabled={!receiptNo} onClick={handleGet} data-testid="general-receipt-cancel-get-button">Get</button>
        </div>
      </div>
      {loading && <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '8px' }}>Loading receipt details...</div>}
      {loadError && <div style={{ fontSize: '11px', color: inlineNoticeColor(loadError), marginTop: '8px' }}>{loadError}</div>}

      {header && (
        <>
          <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Receipt Details</div>
              <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Read-only details loaded from the selected receipt</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>CAO Name</label>
              <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={header.caoname || ''} readOnly data-testid="general-receipt-cancel-cao-name" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Group Code</label>
              <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={header.groupcode || ''} readOnly />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Ticket No.</label>
              <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={header.ticketno || ''} readOnly />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Subscriber Name</label>
              <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={header.contact_name || ''} readOnly />
            </div>
          </div>
        </>
      )}

      {header && (
        <>
          <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Voucher Line to Reverse</div>
              <div style={{ fontSize: '11px', color: '#9CA3AF' }}>EasyChit reverses one voucher line at a time — select the one to cancel</div>
            </div>
          </div>

          {rows.length === 0 ? (
            <div style={{ padding: '12px', fontSize: '13px', color: '#9CA3AF', border: '1px dashed #E5E7EB', borderRadius: '6px' }}>
              No transaction details found for this receipt.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }} data-testid="general-receipt-cancel-table">
              <thead>
                <tr style={{ borderBottom: '1px solid #E5E7EB', textAlign: 'left', color: '#6B7280', fontSize: '11px' }}>
                  <th style={{ padding: '8px', width: '32px' }}></th>
                  <th style={{ padding: '8px' }}>Payment No.</th>
                  <th style={{ padding: '8px' }}>Payment Date</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>Ledger Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #F3F4F6', background: selectedIdx === i ? '#EFF6FF' : undefined }}>
                    <td style={{ padding: '8px' }}>
                      <input type="radio" name="general-receipt-cancel-row" checked={selectedIdx === i} onChange={() => setSelectedIdx(i)} data-testid={`general-receipt-cancel-row-${i}`} />
                    </td>
                    <td style={{ padding: '8px' }}>{r.payment_number}</td>
                    <td style={{ padding: '8px' }}>{r.payment_date}</td>
                    <td style={{ padding: '8px', textAlign: 'right', color: '#DC2626', fontWeight: '600' }}>{Number(r.ledger_amount).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

      <SectionLock locked={!header || selectedIdx < 0} hint="Select a voucher line to reverse in section C first">
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
        <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>D</div>
        <div>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason for Cancellation</div>
          <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Minimum 10 characters — this will be recorded in the audit log</div>
        </div>
      </div>
      <textarea
        className="input-field"
        style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
        rows={3}
        maxLength={200}
        placeholder="Enter reason (min 10 characters)..."
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        data-testid="general-receipt-cancel-reason"
      />
      <div style={{ fontSize: '11px', color: reason.trim().length >= 10 ? '#059669' : '#9CA3AF', marginTop: '2px', textAlign: 'right' }}>
        {reason.length} / 200
      </div>
      {reasonInvalid && <div style={{ fontSize: '11px', color: '#DC2626' }}>Minimum 10 characters required</div>}
      </SectionLock>
      {submitError && <div style={{ fontSize: '12px', color: '#DC2626', marginTop: '8px' }} data-testid="general-receipt-cancel-error">{submitError}</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
        <button type="button" onClick={handleClear} className="btn-secondary" data-testid="general-receipt-cancel-clear-button">Clear</button>
        <button type="button" onClick={handleSave} className="btn-primary" disabled={selectedIdx < 0 || saving} data-testid="general-receipt-cancel-save-button">
          {saving ? 'Cancelling...' : 'Cancel Receipt'}
        </button>
      </div>
    </div>
  );
}

export default GeneralReceiptCancelForm;
